import { useCallback, useEffect, useState } from 'react'
import CustomDataGrid from './Customers/CustomDataGrid'
import TabbedForm from '@cbgms/components/layout/TabbedForm'
import TabItem from '@cbgms/components/form/Tabs/TabItem'
import { FormLayoutOuterProps } from '@cbgms/components/layout/FormLayout'
import EcommerceInfo from '../Profile/EcommerceInfo'
import ERPData from '../Profile/ERPData'
import { ICompany, IShopByCompany } from '@cbgms/api'
import {
  conceptsResponseTable,
  ecommerceInterface,
  extractedDataInterface,
  salesAgreementsResponseTable
} from '@cbgms/api/modules/customers'
import Concepts from './Concepts'
import SalesAgreements from './SalesAgreements'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Box, Fade, IconButton, Modal, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { fetchConcepts, fetchContracts } from './state/actions'
import { Dispatch } from 'redux'
import CloseIcon from '@mui/icons-material/Close'
import store from './state/store'

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  paper: {
    backgroundColor: theme.palette.grey[200],
    borderRadius: '5px',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    width: '80%',
    height: '90%',
    position: 'relative'
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(3),
    right: theme.spacing(12)
  },
  container: {
    display: 'flex'
  },
  innerContainer: {
    width: '100%',
    position: 'relative'
  },
  closeButtonStyle: {
    backgroundColor: '#f0f0f0'
  },
  marginDiv: {
    margin: '15px 0px 20px 0px'
  },
  customerInfoSeparator: {
    display: 'inline-block',
    margin: '0 8px',
    '&::after': {
      content: '"\\007C"' // Unicode for vertical bar
    }
  }
}))

interface OuterProps {
  shops: IShopByCompany[]
}

interface Props extends OuterProps, Omit<FormLayoutOuterProps<ICompany>, 'DeleteModalContainer'> {}

const Profile: React.FC<Props> = ({ form, shops, initialValues, middleMenuItems, ...rest }) => {
  const classes = useStyles()
  const [isShow, setIsShow] = useState<boolean>(false)
  const [customerNo, setCustomerNo] = useState<string | undefined>()
  const [companyName, setCompanyName] = useState<string | undefined>()
  const [selectedRow, setSelectedRow] = useState<ecommerceInterface | null>(null)
  const [extractedConcept, setExtractedConcept] = useState<extractedDataInterface | null>(null)
  const initialConceptsData: conceptsResponseTable = []
  const [conceptsData, setConceptsData] = useState<conceptsResponseTable>(initialConceptsData as conceptsResponseTable)
  const initialSalesAgreementsData: salesAgreementsResponseTable = []
  const [salesAgreementsData, setSalesAgreementsData] = useState<salesAgreementsResponseTable>(initialSalesAgreementsData)
  const { t } = useTranslation()
  const dispatch = useDispatch<Dispatch>()
  const getData1 = (data: {
    extractedDataPass: extractedDataInterface | null
    ecomTabData: ecommerceInterface | null
    isShow: boolean
    custNo: string
    custName: string | undefined
  }) => {
    setExtractedConcept(data.extractedDataPass)
    setSelectedRow(data.ecomTabData)
    setIsShow(data.isShow)
    setCustomerNo(data.custNo)
    setCompanyName(data.custName)
  }

  useEffect(() => {
    const syncSelectedCustomer = () => {
      const selectedCustomer = store.getState().SelectedCustomer

      if (selectedCustomer) {
        setCustomerNo(selectedCustomer.custNo)
        setCompanyName(selectedCustomer.custName)
        setExtractedConcept(selectedCustomer.extractedConcept as extractedDataInterface | null)
        setSelectedRow((selectedCustomer.ecomTabData as ecommerceInterface | null) || null)
      }
    }

    syncSelectedCustomer()
    const unsubscribe = store.subscribe(syncSelectedCustomer)

    return () => {
      unsubscribe()
    }
  }, [])
  const fetchNew = useCallback(
    (extractedConcept: { CustEosID: string; OuName: string; AppId: string }) => {
      dispatch(fetchConcepts(extractedConcept))
        .then(response => {
          setConceptsData(response?.Data || [])
        })
        .catch(console.error)
    },
    [dispatch]
  )

  const fetchContractsData = useCallback(
    (extractedData: { CustEosID: string; OuName: string; AppId: string }) => {
      dispatch(fetchContracts({ CustEosId: extractedData.CustEosID, AppId: extractedData.AppId }))
        .then(response => {
          const data = response?.Data
          setSalesAgreementsData(Array.isArray(data) ? data : [])
        })
        .catch(console.error)
    },
    [dispatch]
  )

  useEffect(() => {
    if (extractedConcept) {
      fetchNew(extractedConcept)
      fetchContractsData(extractedConcept)
    }
  }, [extractedConcept, fetchNew, fetchContractsData])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('#style2')) {
        setIsShow(false)
      }
    }

    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  return (
    <div className={classes.container}>
      <div className={classes.innerContainer}>
        <CustomDataGrid onUpdate={getData1} />
      </div>

      <Modal open={isShow} onClose={() => setIsShow(false)} closeAfterTransition className={classes.modal}>
        <>
          <IconButton className={`${classes.closeButton} ${classes.closeButtonStyle}`} onClick={() => setIsShow(false)} size='small'>
            <CloseIcon />
          </IconButton>
          <Fade in={isShow}>
            <Box className={classes.paper} onClick={e => e.stopPropagation()}>
              <Typography variant='h6' component='h2'>
                {t('AppLabels.CustomerInformation')}
              </Typography>
              {extractedConcept && (
                <div className={classes.marginDiv}>
                  {customerNo}
                  <span className={classes.customerInfoSeparator} />
                  {extractedConcept.CustEosID}
                  <span className={classes.customerInfoSeparator} />
                  {companyName}
                  <span className={classes.customerInfoSeparator} />
                  {extractedConcept.OuName}
                </div>
              )}
              <TabbedForm form='' initialValues={[]} {...rest} showButtons={false}>
                <TabItem name='eCommerceInfoTab' title={t('PopUps.eCommerceInfoTab')}>
                  {selectedRow && <EcommerceInfo eData={selectedRow} />}
                </TabItem>
                <TabItem name='conceptsTab' title={t('PopUps.conceptsTab')}>
                  <Concepts cData={conceptsData} />
                </TabItem>
                <TabItem name='erpDataTab' title={t('PopUps.erpDataTab')}>
                  {selectedRow && <ERPData erpData={selectedRow} />}
                </TabItem>
                <TabItem name='salesAgreementsTab' title={t('PopUps.salesAgreementsTab')}>
                  <SalesAgreements sData={salesAgreementsData} />
                </TabItem>
              </TabbedForm>
            </Box>
          </Fade>
        </>
      </Modal>
    </div>
  )
}

export default Profile
